import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import axios from "axios";
import "../styles/userlist.css";
import { Icon } from "@iconify/react";
import { Link, useLocation, useHistory } from "react-router-dom";
import "../styles/general.css";
import "../styles/main.css";
import { URL } from "../core/config";
import { format } from "date-fns";
import eventService from "../core/services/EventService";
import { Modal } from "react-bootstrap";
import CreateEvent from "./CreateEvent";
import "../styles/CreateEventWizard.css";
import { useAuth } from "../core/AuthContext";
import useDepartmentFilter from "./hooks/useDepartmentFilter";
import usePagination from "./hooks/usePagination";
import useUsers from "./hooks/useUsers";
import useFetchUsersByIds, {
  collectEventUserRefs,
  offRosterUserIds,
} from "./hooks/useFetchUsersByIds";
import { useSemester } from '../core/SemesterContext';
import NavHeader from "./NavHeader";
import CustomAlert from "./CustomAlert";
import CustomAlertWithButtons from "./CustomAlertWithButtons";
import DepartmentFilterBar from "./departments/DepartmentFilterBar";
import { canAccessAdminStats, canManageGeneralMeetings } from "../core/utils/permissionUtils";
import {
  getDepartmentId,
  departmentMatchesFilter,
  resolveDepartmentFilterValues,
} from "../core/constants/departments";
import { useDepartments } from "../core/DepartmentsContext";
import EventListSection, { getEventId } from "./events/EventListSection";

const eventsURL = URL + "/events";

function filterEventsList(allEvents,
  { searchTerm = "", departments = [], showOnlyMyEvents = false, currUserId = null } = {}
) {
  let filtered = [...allEvents];

  if (searchTerm.trim()) {
    const hasAccent = /[\u0300-\u036f]/.test(searchTerm.normalize("NFD"));
    const search = searchTerm.trim();

    const normalize = (str) =>
      str?.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase() || "";

    filtered = filtered.filter((event) => {
      const name = event.name || "";
      const description = event.description || "";

      if (hasAccent) {
        return (
          name.toLowerCase().includes(search.toLowerCase()) ||
          description.toLowerCase().includes(search.toLowerCase())
        );
      }

      return (
        normalize(name).includes(normalize(search)) ||
        normalize(description).includes(normalize(search))
      );
    });
  }

  if (showOnlyMyEvents) {
    filtered = filtered.filter((event) => {
      if (!currUserId) return false;

      return event.mos?.some((id) => String(id) === String(currUserId))
      || event.vmos?.some((id) => String(id) === String(currUserId))
      || event.participants?.some((id) => String(id) === String(currUserId))
      || event.alumni?.some((id) => String(id) === String(currUserId))
      || String(event.vmo) === String(currUserId);
    });
  }

  if (departments.length > 0) {
    filtered = filtered.filter((event) =>
      departments.every((filterDep) =>
        event.departments?.some((eventDep) => departmentMatchesFilter(eventDep, filterDep))
      )
    );
  }

  return filtered;
}

function EventsList() {
  const authContext = useAuth();
  const contextUser = authContext.user;
  const { departments: catalog } = useDepartments();
  const [allSemesterEvents, setAllSemesterEvents] = useState([]);
  const eventUserRefs = useMemo(
    () => collectEventUserRefs(allSemesterEvents),
    [allSemesterEvents],
  );
  const {
    users: rosterUsers,
    findUser,
    loading: usersLoading,
    formatUserDisplayName,
    semesterUsersLoaded,
  } = useUsers();
  const offRosterIds = useMemo(
    () => offRosterUserIds(eventUserRefs, rosterUsers, semesterUsersLoaded),
    [eventUserRefs, rosterUsers, semesterUsersLoaded],
  );
  const { findUserById: findOffRosterUserById } = useFetchUsersByIds(offRosterIds);
  const [semesterEventsLoaded, setSemesterEventsLoaded] = useState(false);

  // Track if component is mounted
  const isMountedRef = useRef(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [sortOrder, setSortOrder] = useState("recent");
  const searchInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyMyEvents, setShowOnlyMyEvents] = useState(false);
  const [filteredEvents, setFilteredEvents] = useState([]);
  const filterOptionsRef = useRef({ searchTerm: "", departments: [],
    showOnlyMyEvents: false, currUserId: null});
  // General Meetings are excluded from this list by default (they don't count
  // as regular events); users with MANAGE_GENERAL_MEETING see them by default.
  const [showGeneralMeetings, setShowGeneralMeetings] = useState(false);
  const gmToggleInitializedRef = useRef(false);

  const canManageGm = useMemo(
    () => canManageGeneralMeetings(contextUser),
    [contextUser],
  );

  useEffect(() => {
    if (!contextUser || gmToggleInitializedRef.current) return;

    gmToggleInitializedRef.current = true;
    if (canManageGeneralMeetings(contextUser)) {
      setShowGeneralMeetings(true);
      setSemesterEventsLoaded(false);
    }
  }, [contextUser]);

  const location = useLocation();
  const history = useHistory();

  // For CustomAlert
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    type: '',
    message: ''
  });

  const [alertActionInfo, setAlertActionInfo] = useState({
    show: false,
    title: '',
    type: '',
    message: '',
    btn1Action: () => { },
    btn2Action: () => { },
    btn1Text: '',
    btn2Text: ''
  });

  const semesterContext = useSemester();
  // currentSemester is the Semester selected not the current active semester
  const semester = semesterContext.currentSemester;

  const handleShow = () => setShowModal(true);
  const closeCreateModal = useCallback(() => {
    setShowModal(false);
  }, []);

  const toggleEventInfo = useCallback(
    (eventId) => {
      setSelectedEventId(selectedEventId === eventId ? null : eventId);
    },
    [selectedEventId]
  );

  const memoizedFormatDate = useCallback((dateString) => {
    if (dateString)
      return format(new Date(dateString), "EEEE, dd.MM.yyyy @ HH:mm");
    else return "";
  }, []);

  // Fetch all semester events once
  // Fetch all semester events once
  const fetchAllSemesterEvents = useCallback(async (options = {}) => {
    if (!semester?._id) return;

    const includeGeneralMeetings =
      options.includeGeneralMeetings ?? showGeneralMeetings;

    try {
      if (isMountedRef.current) {
        setLoading(true);
      }

      const params = new URLSearchParams();
      params.append("semester", String(semester._id));
      if (includeGeneralMeetings) {
        params.append("includeGeneralMeetings", "true");
      }

      const response = await axios.get(`${eventsURL}/list?${params.toString()}`);
      const events = response.data || [];

      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setAllSemesterEvents(events);
        setFilteredEvents(
          filterEventsList(events, filterOptionsRef.current)
        );
        setSemesterEventsLoaded(true);
        setError(null);
      }
    } catch (err) {
      console.error("Error fetching semester events:", err);
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setError(err);
        setAllSemesterEvents([]);
        setFilteredEvents([]);
      }
    } finally {
      // Only update state if component is still mounted
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [semester?._id, showGeneralMeetings]);

  const toggleShowGeneralMeetings = useCallback((event) => {
    setShowGeneralMeetings(event.target.checked);
    setSemesterEventsLoaded(false);
  }, []);

  // Filter events function
  const filterEvents = useCallback(
    (options = {}) => filterEventsList(allSemesterEvents, options),
    [allSemesterEvents]
  );

  const sortedEvents = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      if (sortOrder === "recent") {
        return new Date(b.date) - new Date(a.date);
      } else {
        return new Date(a.date) - new Date(b.date);
      }
    });
  }, [filteredEvents, sortOrder]);

  // Pagination
  const eventsPerPage = 20;
  const totalEvents = sortedEvents.length;
  const {
    currentPage,
    setCurrentPage,
    totalPages,
    goToNextPage,
    goToPreviousPage,
  } = usePagination(eventsPerPage, totalEvents);

  const {
    selectedDepartments,
    handleDepartmentChange,
    setSelectedDepartments,
  } = useDepartmentFilter(setCurrentPage);

  useEffect(() => {
    filterOptionsRef.current = {
      searchTerm: searchQuery,
      departments: selectedDepartments,
      showOnlyMyEvents,
      currUserId: contextUser?._id
    };
  }, [searchQuery, selectedDepartments, showOnlyMyEvents, contextUser?._id]);

  const handleDeleteEvent = useCallback(async (eventId) => {
    setAlertActionInfo({
      show: true,
      title: 'Do you want to delete this event permanently?',
      type: 'error',
      message: 'This action will delete all the information about this event. Are you sure you want to proceed? This cannot be undone.',
      btn2Action: async () => {
        try {
          await eventService.deleteEvent(eventId);
          if (isMountedRef.current) {
            setAllSemesterEvents((prevEvents) => {
              const nextEvents = prevEvents.filter(
                (event) => getEventId(event) !== String(eventId)
              );
              setFilteredEvents(
                filterEventsList(nextEvents, filterOptionsRef.current)
              );
              return nextEvents;
            });
            setAlertInfo({
              show: true,
              type: 'success',
              message: 'Event deleted successfully!',
            });
          }
        } catch (error) {
          if (isMountedRef.current) {
            setAlertInfo({
              show: true,
              type: 'error',
              message: `Failed to delete event: ${error.message}`,
            });
          }
        } finally {
          setAlertActionInfo((prevState) => ({
            ...prevState,
            show: false,
          }));
        }
      },
      btn1Action: () => {
        setAlertActionInfo((prevState) => ({
          ...prevState,
          show: false,
        }));
      },
      btn1Text: 'Not now',
      btn2Text: 'Delete',
    });
  }, []);

  const handleEventCreated = useCallback(async (createdEvent) => {
    setShowModal(false);
    setCurrentPage(1);

    const isGeneralMeeting = createdEvent?.isGeneralMeeting === true;
    const includeGeneralMeetings = isGeneralMeeting && canManageGm
      ? true
      : showGeneralMeetings;

    if (isGeneralMeeting && canManageGm && !showGeneralMeetings) {
      setShowGeneralMeetings(true);
    }

    const eventId = createdEvent?._id ? String(createdEvent._id) : null;
    if (!eventId) {
      setAlertInfo({
        show: true,
        type: 'error',
        message: 'Event was created but no ID was returned. Please refresh the list.',
      });
      await fetchAllSemesterEvents({ includeGeneralMeetings });
      return;
    }

    let serverEvent = null;
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        serverEvent = await eventService.getEventById(eventId);
        break;
      } catch {
        if (attempt < 2) {
          await new Promise((resolve) => setTimeout(resolve, 500 * (attempt + 1)));
        }
      }
    }

    await fetchAllSemesterEvents({ includeGeneralMeetings });

    setAlertInfo({
      show: true,
      type: serverEvent ? 'success' : 'warning',
      message: serverEvent
        ? isGeneralMeeting
          ? 'General Meeting was successfully created!'
          : 'The event was successfully created!'
        : 'Event may have been created. Please wait a moment before editing, or refresh the list.',
    });
  }, [canManageGm, fetchAllSemesterEvents, setCurrentPage, showGeneralMeetings]);

  const departmentCounts = useMemo(() => {
    const counts = {};
    catalog.filter((d) => d.showInFilters !== false).forEach((dept) => {
      const key = getDepartmentId(dept) || dept.name;
      counts[key] = allSemesterEvents.filter((event) =>
        event.departments?.some((eventDep) => departmentMatchesFilter(eventDep, key))
      ).length;
    });
    return counts;
  }, [allSemesterEvents, catalog]);

  // Clear events when semester changes
  useEffect(() => {
    if (semester?._id) {
      setAllSemesterEvents([]);
      setSemesterEventsLoaded(false);
      setFilteredEvents([]);
      setSearchQuery('');
      setSelectedDepartments([]);
      setShowOnlyMyEvents(false);
    }
  }, [semester?._id, setSelectedDepartments]);


  // Helper to get formatted user display name by ID
  const resolveEventUser = useCallback(
    (id) => findUser(id) || findOffRosterUserById(id),
    [findUser, findOffRosterUserById],
  );

  const getUserDisplayName = useCallback(
    (id) => {
      const user = resolveEventUser(id);
      return user ? formatUserDisplayName(user) : "Unknown User";
    },
    [resolveEventUser, formatUserDisplayName],
  );

  const getUserById = useCallback(
    (id) => resolveEventUser(id),
    [resolveEventUser],
  );

  // Helper to check if contextUser is MO, VMO, or participant
  //  const isUserPartOfEvent = (event) => {
  //    if (!contextUser?._id) return false;
  //    if (event.mo === contextUser._id) return true;
  //    if (Array.isArray(event.vmos) && event.vmos.some(vmoId => vmoId === contextUser._id)) return true;
  //    if (Array.isArray(event.participants) && event.participants.some(pid => pid === contextUser._id)) return true;
  //    return false;
  //  };

  // Fetch events when semester changes
  useEffect(() => {
    if (semester?._id && !semesterEventsLoaded) {
      fetchAllSemesterEvents();
    }
  }, [semester?._id, semesterEventsLoaded, fetchAllSemesterEvents]);


  // 4. Add a useEffect to hydrate state from URL on initial load
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    const deps = params.get('departments');
    const sort = params.get('sort') || 'recent';
    const page = parseInt(params.get('page') || '1', 10);
    const mine = params.get('mine') === 'true';

    setSearchQuery(q);
    setSelectedDepartments(deps ? resolveDepartmentFilterValues(deps.split(','), catalog) : []);
    setSortOrder(sort);
    setCurrentPage(Number.isNaN(page) ? 1 : page);
    setShowOnlyMyEvents(mine);

    if (searchInputRef.current) {
      searchInputRef.current.value = q;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Runs only once on mount

  // 5. Add a useEffect to update the URL whenever filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) {
      params.set('q', searchQuery);
    }
    if (selectedDepartments.length > 0) {
      params.set('departments', selectedDepartments.join(','));
    }
    if (sortOrder !== 'recent') {
      params.set('sort', sortOrder);
    }
    if (currentPage > 1) {
      params.set('page', String(currentPage));
    }
    if (showOnlyMyEvents) {
      params.set('mine', showOnlyMyEvents);
    }

    // Replace the current history entry with the new URL
    history.replace({
      pathname: location.pathname,
      search: params.toString()
    });
  }, [searchQuery, selectedDepartments, sortOrder, currentPage, history, location.pathname, showOnlyMyEvents]);


  // Filter events when search query or departments change
  useEffect(() => {
    if (semesterEventsLoaded) {
      const filtered = filterEvents({
        searchTerm: searchQuery,
        departments: selectedDepartments,
        showOnlyMyEvents,
        currUserId: contextUser?._id
      });
      setFilteredEvents(filtered);
    }
  }, [searchQuery, selectedDepartments, semesterEventsLoaded, filterEvents,
    allSemesterEvents, showOnlyMyEvents, contextUser?._id]);

  // Paginated events
  const paginatedEvents = useMemo(() => {
    const startIndex = (currentPage - 1) * eventsPerPage;
    const endIndex = startIndex + eventsPerPage;
    return sortedEvents.slice(startIndex, endIndex);
  }, [sortedEvents, currentPage, eventsPerPage]);

  useEffect(() => {
    if (!loading && searchQuery && searchInputRef.current) {
      if (document.activeElement !== searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }
  }, [loading, searchQuery]);

  // Add this cleanup effect before the return statement
  useEffect(() => {
    return () => {
      // Mark component as unmounted
      isMountedRef.current = false;
    };
  }, []);

  if (error) return <div>Error: {error.message}</div>;

  if (error) return <div>Error: {error.message}</div>;

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === "recent" ? "oldest" : "recent");
  };

  const toggleShowOnlyMyEvents = () => {
    setCurrentPage(1);
    setShowOnlyMyEvents(!showOnlyMyEvents);
  };

  const handleSearchChange = (e) => {
    const { value } = e.target;
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedDepartments([]);
    setSortOrder('recent');
    setCurrentPage(1);
    setShowOnlyMyEvents(false);
    if (searchInputRef.current) {
      searchInputRef.current.value = '';
    }
  };

  return (
    <div className="page-container">
      <Modal
        centered
        size="lg"
        show={showModal}
        onHide={closeCreateModal}
        contentClassName="event-wizard-modal-content"
      >
        <Modal.Body className="event-wizard-modal-body">
          <CreateEvent onEventCreated={handleEventCreated} onCancel={closeCreateModal} />
        </Modal.Body>
      </Modal>

      <NavHeader />

      <div className="page-title-row">
        <h1 className="page-title page-title-icon">
          <span className="main-section-icon">
            <span
              className="main-section-icon-fill"
              style={{ background: "rgba(0, 174, 239, 0.15)", color: "#00aeef" }}
            >
              <Icon icon="line-md:calendar" className="icon" />
            </span>
          </span>
          Our Wonderful Events!
        </h1>

        <div className="title-action-buttons">
          {canAccessAdminStats(contextUser) && (
            <Link
              to="/admin-stats-manager"
              className="title-icon-button"
              aria-label="Admin Stats Manager"
            >
              <Icon icon="mdi:chart-box" />
            </Link>
          )}

          <button
            type="button"
            className="title-icon-button"
            onClick={handleShow}
            aria-label="Create Event"
          >
            <Icon icon="material-symbols:add" />
          </button>
        </div>
      </div>

      <div className="user-list-container events-page">
        <div className="user-list-lateral-section">
          <div className="search-notification-layout">
            <div className="search-bar">
              <input
                type="text"
                placeholder="Search events..."
                value={searchQuery}
                onChange={handleSearchChange}
                ref={searchInputRef}
              />
              <i className="uil uil-search search-icon"></i>
            </div>

            <button
              type="button"
              className={`sort-icon-button ${sortOrder === "recent" ? "active" : ""}`}
              onClick={toggleSortOrder}
              aria-label={`Sort by: ${sortOrder === "recent" ? "Oldest First" : "Newest First"}`}
            >
              <Icon
                icon={sortOrder === "recent" ? "material-symbols:keyboard-arrow-down" : "material-symbols:keyboard-arrow-up"}
              />
            </button>

            <button
              type="button"
              className={`my-events-icon-button ${showOnlyMyEvents ? "active" : ""}`}
              onClick={toggleShowOnlyMyEvents}
              aria-label={showOnlyMyEvents ? "Show all events" : "Show only my events"}
            >
              <Icon icon={showOnlyMyEvents ? "material-symbols:person-raised-hand-outline-rounded" : "akar-icons:person"} />
            </button>
          </div>

          {canManageGm && (
            <label className="events-general-meetings-toggle">
              <span className="events-general-meetings-toggle-label">
                {showGeneralMeetings ? "Showing General Meetings" : "General Meetings Hidden"}
              </span>
              <span className="events-general-meetings-switch">
                <input
                  type="checkbox"
                  checked={showGeneralMeetings}
                  onChange={toggleShowGeneralMeetings}
                  aria-label={showGeneralMeetings ? "Hide general meetings" : "Show general meetings"}
                />
                <span className="events-general-meetings-switch-track">
                  <span className="events-general-meetings-switch-thumb" />
                </span>
              </span>
            </label>
          )}

          <div className="list-action-buttons">
            {canAccessAdminStats(contextUser) && (
              <Link to="/admin-stats-manager" className="button list-button admin-button">
                <Icon icon="mdi:chart-box" className="button-icon" />
                Admin Stats Manager
              </Link>
            )}

            <button
              type="button"
              className="button list-button create-button"
              onClick={handleShow}
            >
              <Icon icon="material-symbols:add" className="button-icon" />
              Create Event
            </button>
          </div>

          <button
            className={`button list-button sort-button ${sortOrder === "recent" ? "active" : ""}`}
            onClick={toggleSortOrder}
          >
            <Icon
              icon={sortOrder === "recent" ? "material-symbols:keyboard-arrow-down" : "material-symbols:keyboard-arrow-up"}
              className="button-icon"
            />
            Sort by: {sortOrder === "recent" ? "Oldest First" : "Newest First"}
          </button>

          {/*MY Events Button*/}
          <button
            className={`button list-button my-events-button ${showOnlyMyEvents ? "active" : ""}`}
            onClick={toggleShowOnlyMyEvents}
          >
            <Icon
              icon={
                showOnlyMyEvents
                  ? "material-symbols:person-raised-hand-outline-rounded"
                  : "akar-icons:person"
              }
              className="button-icon"
            />
            My Events
          </button>

          <DepartmentFilterBar
            selectedDepartments={selectedDepartments}
            onDepartmentChange={handleDepartmentChange}
            departmentCounts={departmentCounts}
          />
          {(searchQuery || selectedDepartments.length > 0 || showOnlyMyEvents) && (
            <button
              className="button list-button clear-button"
              onClick={clearFilters}
            >
              <Icon icon="material-symbols:clear-all" className="button-icon" />
              Clear Filters
            </button>
          )}
        </div>
        <EventListSection
          loading={loading || usersLoading || !semester?._id}
          events={allSemesterEvents}
          paginatedEvents={paginatedEvents}
          selectedEventId={selectedEventId}
          toggleEventInfo={toggleEventInfo}
          contextUser={contextUser}
          handleDeleteEvent={handleDeleteEvent}
          formatDate={memoizedFormatDate}
          currentPage={currentPage}
          totalPages={totalPages}
          goToNextPage={goToNextPage}
          goToPreviousPage={goToPreviousPage}
          sortedEvents={sortedEvents}
          getUserDisplayName={getUserDisplayName}
          getUserById={getUserById}
          catalog={catalog}
        />
        {/* Alert that shows upon event submission */}
        {alertInfo.show && (
          <CustomAlert
            type={alertInfo.type}
            message={alertInfo.message}
            autoDismiss={true}
            timeout={4000}
            onClose={() => setAlertInfo({ show: false, type: '', message: '' })}
          />
        )}

        {alertActionInfo.show && (
          <CustomAlertWithButtons
            title={alertActionInfo.title}
            type={alertActionInfo.type}
            message={alertActionInfo.message}
            onButton1Click={alertActionInfo.btn1Action}
            onButton2Click={alertActionInfo.btn2Action}
            button1Text={alertActionInfo.btn1Text}
            button2Text={alertActionInfo.btn2Text}
          />
        )}
      </div>
    </div>
  );
}

export default EventsList;
